import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import { publicAPI } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { useSettings } from '../../App';
import { getTileUrl, getTileAttribution } from '../../lib/mapConfig';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» skin for Page Builder blocks (s140). BlockRenderer switches to
// these when a page is rendered inside <BlockSkinContext.Provider value="modern">
// (ModernPage). Same data and config as the default blocks — only the look
// changes: brand-token prose, quote-style testimonial, greyscale map with
// numbered pins, service cards, 3px image corners.
// ─────────────────────────────────────────────────────────────────────────

export const BlockSkinContext = React.createContext(null);

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const API = process.env.REACT_APP_BACKEND_URL;
const src = (v) => (v ? (v.startsWith('/api') ? `${API}${v}` : v) : null);
const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

// Inline text colours: greys/black pasted from old content are dropped so the
// text follows the brand tokens; real colours the editor chose (e.g. a blue
// heading) are kept.
const HEX = (h) => { h = h.replace('#', ''); if (h.length === 3) h = h.split('').map(c => c + c).join(''); return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)); };
function dropNeutralColors(html) {
  return String(html).replace(/(^|[;\s"'])color:\s*(rgba?\(([^)]*)\)|#[0-9a-fA-F]{3,6})\s*;?/g, (m, pre, val, rgb) => {
    const c = rgb ? rgb.split(',').slice(0, 3).map(n => parseFloat(n)) : HEX(val);
    if (c.some(Number.isNaN)) return m;
    return Math.max(...c) - Math.min(...c) < 60 ? pre : m;
  });
}

// Old content points at uploads that no longer exist (lost before the
// migration) — a missing picture is dropped instead of showing a broken box.
const hideBroken = (el) => { if (el) el.style.display = 'none'; };

export function ModernRichText({ config }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const imgs = ref.current ? [...ref.current.querySelectorAll('img')] : [];
    imgs.forEach(im => {
      if (im.complete && im.naturalWidth === 0 && im.src) hideBroken(im);
      else im.addEventListener('error', () => hideBroken(im), { once: true });
    });
  }, [config.content]);
  // An empty editor block (e.g. "<p></p>") would still take a gap in the column.
  if (!config.content || (!plain(config.content) && !/<(img|iframe|video|table)/i.test(config.content))) return null;
  return <div ref={ref} className="mip-prose" dangerouslySetInnerHTML={{ __html: dropNeutralColors(String(config.content).replace(/&nbsp;/g, ' ')) }} />;
}

export function ModernImage({ config }) {
  const [broken, setBroken] = React.useState(false);
  const s = src(config.src);
  if (!s || broken) return null;
  const fig = (
    <figure className="m-0">
      <img src={s} alt={config.alt || ''} loading="lazy" onError={() => setBroken(true)} className="w-full rounded-[3px]" data-testid="block-image" />
      {config.caption && <figcaption className="text-[13px] mt-2" style={{ color: 'var(--color-body-text, #475569)' }}>{config.caption}</figcaption>}
    </figure>
  );
  return config.link ? <a href={config.link} target="_blank" rel="noopener noreferrer">{fig}</a> : fig;
}

export function ModernTestimonials() {
  const tt = useT();
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => {
    publicAPI.getTestimonials().then(r => setItems((r.data || []).filter(t => t.visible !== false && tt(t.content)).sort((a, b) => (a.order || 0) - (b.order || 0)))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const go = useCallback((d) => setI(p => (p + d + items.length) % items.length), [items.length]);
  if (!items.length) return null;
  const t = items[i];
  const photo = src(t.image);
  return (
    <figure className="m-0 flex flex-col gap-5 p-6 lg:p-7 rounded-[3px] border" style={{ borderColor: 'rgba(20,26,58,.12)', background: '#fff' }} data-testid="block-legends-carousel">
      <span aria-hidden="true" style={{ fontFamily: SERIF, fontSize: '3.2rem', lineHeight: 0.6, color: 'var(--color-accent, #ff9c1b)' }}>&ldquo;</span>
      <blockquote className="m-0 italic" style={{ fontFamily: SERIF, fontSize: '1.2rem', lineHeight: 1.5, color: 'var(--color-heading, #1a2332)' }} data-testid="testimonial-quote">{plain(tt(t.content)).replace(/^["“]|["”]$/g, '')}</blockquote>
      <figcaption className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-3 min-w-0">
          {photo && <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover flex-none" />}
          <span className="flex flex-col min-w-0">
            <span className="font-semibold text-sm truncate" style={{ color: 'var(--color-heading, #1a2332)' }} data-testid="testimonial-author-name">{tt(t.name)}</span>
            {tt(t.title) && <span className="text-xs truncate" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(t.title)}</span>}
          </span>
        </span>
        {items.length > 1 && (
          <span className="flex items-center gap-2 flex-none">
            <span className="text-xs font-bold tabular-nums" style={{ color: 'rgba(20,26,58,.45)' }}>{i + 1}/{items.length}</span>
            <button type="button" aria-label="Previous testimonial" onClick={() => go(-1)} className="w-8 h-8 grid place-items-center rounded-full border transition-transform duration-150 active:scale-95" style={{ borderColor: 'rgba(20,26,58,.18)', color: 'var(--color-heading, #1a2332)' }}><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" aria-label="Next testimonial" onClick={() => go(1)} className="w-8 h-8 grid place-items-center rounded-full border transition-transform duration-150 active:scale-95" style={{ borderColor: 'rgba(20,26,58,.18)', color: 'var(--color-heading, #1a2332)' }}><ChevronRight className="w-4 h-4" /></button>
          </span>
        )}
      </figcaption>
    </figure>
  );
}

export function ModernServiceList() {
  const tt = useT();
  const [list, setList] = useState([]);
  useEffect(() => { publicAPI.getServices().then(r => setList((r.data || []).filter(s => s.visible !== false))).catch(() => {}); }, []);
  if (!list.length) return null;
  return (
    <div className="grid gap-8 sm:grid-cols-2" data-testid="block-service-list">
      {list.map(s => {
        const inner = (
          <>
            <span className="block aspect-[4/3] overflow-hidden rounded-[3px]" style={{ background: GRAPHITE }}>
              {s.image && <img src={src(s.image)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
            </span>
            <span className="font-semibold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.25rem', color: 'var(--color-heading, #1a2332)' }}>{tt(s.title)}</span>
            <span className="text-[15px] leading-relaxed line-clamp-3" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(s.short_description) || tt(s.description))}</span>
          </>
        );
        return s.external_url
          ? <a key={s.id} href={s.external_url} target={s.open_in_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="group flex flex-col gap-3">{inner}</a>
          : <Link key={s.id} to={`/service/${s.id}`} className="group flex flex-col gap-3">{inner}</Link>;
      })}
    </div>
  );
}

const pin = (n) => L.divIcon({ className: 'mm-pin-wrap', html: `<span class="mm-pin"><b>${n}</b></span>`, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -16] });

function Fit({ pts }) {
  const map = useMap();
  useEffect(() => {
    if (!pts.length) return;
    if (pts.length === 1) map.setView([pts[0].lat, pts[0].lng], 5, { animate: false });
    else map.fitBounds(L.latLngBounds(pts.map(p => [p.lat, p.lng])), { padding: [36, 36], maxZoom: 5, animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pts.length]);
  return null;
}

export function ModernMapBlock({ mapType }) {
  const tt = useT();
  const settings = useSettings();
  const [pts, setPts] = useState([]);
  useEffect(() => { publicAPI.getMapLocations(mapType).then(r => setPts((r.data || []).filter(l => l.lat && l.lng))).catch(() => {}); }, [mapType]);
  if (!pts.length) return null;
  const lang = settings.maps_language || 'local';
  return (
    <div className="modern-map relative isolate overflow-hidden rounded-[3px] h-[300px]" data-testid={`block-map-${mapType}`}>
      <MapContainer center={[pts[0].lat, pts[0].lng]} zoom={3} scrollWheelZoom={false} zoomControl={false} style={{ height: '100%', width: '100%' }}>
        <ZoomControl position="topright" />
        <TileLayer url={getTileUrl(lang)} attribution={getTileAttribution(lang)} />
        <Fit pts={pts} />
        {pts.map((p, i) => (
          <Marker key={p.id || i} position={[p.lat, p.lng]} icon={pin(i + 1)}>
            <Popup>
              <strong>{tt(p.title) || tt(p.name)}</strong>
              {tt(p.description) && <p style={{ margin: '4px 0 0' }}>{tt(p.description)}</p>}
              {p.link && <a href={p.link} target={p.open_in_new_tab ? '_blank' : '_self'} rel="noopener noreferrer">Visit</a>}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

// Album cards: cover 4:3, photo count, title, two-line description. Used by the
// gallery_albums block and by the "More albums" row of an album page.
export function ModernAlbumCards({ exclude, limit }) {
  const tt = useT();
  const [albums, setAlbums] = useState([]);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    publicAPI.getGalleryAlbums().then(r => {
      const list = (r.data || []).filter(a => a.id !== exclude);
      setAlbums(list);
      list.forEach(a => publicAPI.getAlbumPhotos(a.id).then(p => setCounts(c => ({ ...c, [a.id]: (p.data.photos || []).length }))).catch(() => {}));
    }).catch(() => {});
  }, [exclude]);
  const shown = limit ? albums.slice(0, limit) : albums;
  if (!shown.length) return null;
  return (
    <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="block-gallery-albums">
      {shown.map(a => (
        <Link key={a.id} to={`/album/${a.id}`} className="group flex flex-col gap-3" data-testid={`album-card-${a.id}`}>
          <span className="block aspect-[4/3] overflow-hidden rounded-[3px]" style={{ background: GRAPHITE }}>
            {a.cover_image && <img src={src(a.cover_image)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
          </span>
          <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>
            {counts[a.id] != null ? `${counts[a.id]} photo${counts[a.id] === 1 ? '' : 's'}` : 'Album'}
          </span>
          <span className="font-semibold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.35rem', lineHeight: 1.2, color: 'var(--color-heading, #1a2332)' }}>{tt(a.title)}</span>
          {plain(tt(a.description)) && <span className="text-[15px] leading-relaxed line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(a.description))}</span>}
        </Link>
      ))}
    </div>
  );
}
